// Shared SVG illustration used by ErrorPage + NotFound. Inline so
// it can read app palette tokens via ``currentColor`` instead of
// shipping a baked-colour asset that breaks in dark mode.
//
// The composition: a sheet of paper (paged surface) with a crumple
// fold and a few drawn-but-incomplete glyph lines, anchored by a
// floating quill. Reads as "writing got interrupted" rather than
// the generic "broken cog" stock error illustration. Sized via
// ``className`` from the caller so each page can pick its own
// scale; defaults to ~12rem.
//
// ``variant``:
//   * "error"    — orange-ish accent (uses ``text-warning``)
//   * "notfound" — primary accent (uses ``text-primary``)

export default function ErrorIllustration({ variant = "error", className = "" }) {
  // The accent ring + quill nib pick up the variant's tint via
  // ``currentColor``. The paper body stays neutral (border-token
  // outline + bg-surface fill) so the illustration sits flush in
  // both light and dark themes.
  const accent =
    variant === "notfound" ? "text-primary" : "text-warning";

  return (
    <svg
      viewBox="0 0 240 200"
      role="img"
      aria-hidden="true"
      className={`${accent} ${className}`}
    >
      {/* Decorative accent halo behind the paper — soft + low-
          contrast so it reads as ambient depth rather than a
          competing shape. */}
      <ellipse
        cx="120"
        cy="160"
        rx="90"
        ry="10"
        className="fill-current opacity-10"
      />

      {/* Paper sheet. Stroke + fill come from the theme so the
          illustration follows the app's surface/border tokens. */}
      <g
        transform="rotate(-4 120 100)"
        className="text-border"
      >
        <path
          d="M 70 30 L 170 30 L 180 50 L 180 170 L 60 170 Z"
          className="fill-[color:var(--color-surface,white)] stroke-current"
          strokeWidth="2"
          strokeLinejoin="round"
        />
        {/* Folded corner — the "crumple". Filled in surfaceAlt so
            it reads as a separate plane of paper. */}
        <path
          d="M 170 30 L 180 50 L 162 50 Z"
          className="fill-[color:var(--color-surfaceAlt,#f4f4f5)] stroke-current"
          strokeWidth="2"
          strokeLinejoin="round"
        />

        {/* Three text lines — uneven, getting shorter, the third
            cut off mid-stroke to signal "writing was interrupted". */}
        <line
          x1="80"
          y1="80"
          x2="160"
          y2="80"
          className="stroke-current opacity-50"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <line
          x1="80"
          y1="100"
          x2="150"
          y2="100"
          className="stroke-current opacity-40"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <line
          x1="80"
          y1="120"
          x2="115"
          y2="120"
          className="stroke-current opacity-30"
          strokeWidth="3"
          strokeLinecap="round"
        />

        {/* The "interrupted" glyph — three trailing dots after the
            short line, sized down to read as a fade. */}
        <circle cx="124" cy="120" r="2" className="fill-current opacity-30" />
        <circle cx="132" cy="120" r="2" className="fill-current opacity-20" />
        <circle cx="140" cy="120" r="2" className="fill-current opacity-10" />
      </g>

      {/* Quill — floating to the right of the paper, tipped down
          toward the cut-off line. Nib picks up the accent so the
          eye lands there first. */}
      <g transform="translate(150 60) rotate(35)">
        {/* Feather barbs — a stack of arcs tapering toward the
            nib. */}
        <path
          d="M 0 0 Q 8 -4 16 0 Q 20 2 16 4 Q 10 6 0 5 Z"
          className="fill-current opacity-60"
        />
        <path
          d="M 14 6 Q 24 4 36 12 Q 40 16 36 18 Q 24 16 12 12 Z"
          className="fill-current opacity-50"
        />
        <path
          d="M 32 18 Q 44 18 56 28 Q 60 32 56 34 Q 40 32 28 26 Z"
          className="fill-current opacity-40"
        />
        {/* Shaft */}
        <line
          x1="0"
          y1="0"
          x2="80"
          y2="46"
          className="stroke-current opacity-70"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        {/* Nib — solid, accent-coloured triangle */}
        <path
          d="M 76 42 L 88 50 L 80 52 Z"
          className="fill-current"
        />
      </g>
    </svg>
  );
}
